/**
 * Byråets innboks, hentet og skrevet.
 *
 * Én prefiks — `qk.agencyEnquiries(orgId)` — som hver skriving invaliderer. Det er den samme
 * avgjørelsen bookingregisteret tok, og av samme grunn: å navngi undermengden riktig per mutasjon
 * er fem regler noen må vedlikeholde, og den ene som blir glemt gir en liste som ligger ett klikk
 * bak.
 */
import {
  bookingEnquiryDtoSchema,
  bookingEnquiryListResponseSchema,
  enquiryOrganizersResponseSchema,
  sharedAdvanceDtoSchema,
  type AnswerBookingEnquiryRequest,
  type AssignBookingEnquiryRequest,
  type BookingEnquiryDto,
  type BookingEnquiryListResponse,
  type EnquiryOrganizersResponse,
  type EnquirySortKey,
  type EnquiryStatus,
  type SharedAdvanceDto,
  type EnterAdvanceRequest,
} from '@tvitvi/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

const PATH = '/booking-enquiries';

export interface EnquiryFilters extends Record<string, unknown> {
  status?: EnquiryStatus | undefined;
  search?: string | undefined;
  /** «Venter på oss» (sak 110.1). Sann eller fraværende — aldri usann, se nøkkelen. */
  waitingOnUs?: true | undefined;
  /** Bare de arkiverte (QA5 E #5). Samme regel: sann eller fraværende. */
  archived?: true | undefined;
  /** Bare én arrangør, ved navnet (F24). */
  organizer?: string | undefined;
  /** QA13 102: «Falt bort» skjult til man ber om det. Sann eller fraværende. */
  hideFellThrough?: true | undefined;
  /** QA13 99: sorteringen API-et kan gjøre selv. Standard er mottatt, nyeste først. */
  sort?: EnquirySortKey | undefined;
  direction?: 'asc' | 'desc' | undefined;
}

/**
 * ÉN forespørsel, slått opp på id — for lenkene som bare kjenner id-en (innboksen, varselet, «kopier
 * lenke»). De må finne artisten forespørselen gjelder før de kan åpne dialogen på artistsiden.
 */
export function enquiryQueryOptions(orgId: string, enquiryId: string) {
  return queryOptions({
    queryKey: qk.agencyEnquiry(orgId, enquiryId),
    queryFn: ({ signal }): Promise<BookingEnquiryDto> =>
      apiRequest({ path: `${PATH}/${enquiryId}`, schema: bookingEnquiryDtoSchema, signal }),
    ...CACHE.list,
  });
}

export function enquiriesQueryOptions(orgId: string, filters: EnquiryFilters = {}) {
  return queryOptions({
    queryKey: qk.agencyEnquiriesList(orgId, filters),
    queryFn: ({ signal }): Promise<BookingEnquiryListResponse> =>
      apiRequest({
        path: PATH,
        query: {
          sort: filters.sort ?? 'created',
          direction: filters.direction ?? 'desc',
          ...(filters.status === undefined ? {} : { status: filters.status }),
          ...(filters.search === undefined ? {} : { search: filters.search }),
          ...(filters.waitingOnUs === undefined ? {} : { waitingOnUs: 'true' }),
          ...(filters.archived === undefined ? {} : { archived: 'true' }),
          ...(filters.organizer === undefined ? {} : { organizer: filters.organizer }),
          ...(filters.hideFellThrough === undefined ? {} : { hideFellThrough: 'true' }),
        },
        schema: bookingEnquiryListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * ALLE FORESPØRSLENE TIL ÉN ARTIST (251.5). Artistsiden leste første side av hele innboksen og
 * filtrerte etterpå, så en artist med eldre forespørsler manglet rader som eksporten hadde. Nå spør
 * den etter artisten og blar gjennom sidene til det ikke er flere — samme svarform som lista.
 */
export function artistEnquiriesQueryOptions(orgId: string, artistId: string) {
  return queryOptions({
    queryKey: qk.agencyArtistEnquiries(orgId, artistId),
    queryFn: async ({ signal }): Promise<BookingEnquiryListResponse> => {
      const items: BookingEnquiryListResponse['items'] = [];
      let cursor: string | null = null;
      do {
        const page: BookingEnquiryListResponse = await apiRequest({
          path: PATH,
          query: {
            sort: 'created',
            direction: 'desc',
            artistId,
            limit: '100',
            ...(cursor === null ? {} : { cursor }),
          },
          schema: bookingEnquiryListResponseSchema,
          signal,
        });
        items.push(...page.items);
        cursor = page.hasMore ? page.nextCursor : null;
      } while (cursor !== null);
      return { items, nextCursor: null, hasMore: false };
    },
    enabled: artistId !== '',
    ...CACHE.list,
  });
}

/** Arrangørene innboksen har forespørsler fra, til filteret (F24). Under forespørslenes prefiks. */
export function enquiryOrganizersQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: qk.agencyEnquiryOrganizers(orgId),
    queryFn: ({ signal }): Promise<EnquiryOrganizersResponse> =>
      apiRequest({
        path: `${PATH}/organizers`,
        schema: enquiryOrganizersResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/* `dealSheets`: byråets avslag på et bud går til avtalearket (C3). */
const invalidates = (orgId: string) => ({
  invalidates: [qk.agencyEnquiries(orgId), qk.dealSheets(orgId)],
});

/**
 * DET OVERFØRINGEN FAKTISK RØRER (QA runde 6, punkt 16 — og synligheten av P0.1).
 *
 * Plukkeren skriver tre steder: mennesker på programposten, rader i advancens seksjoner, og KOPIER
 * av filene. De to siste har hver sin spørring, og fram til 12. september sveipet knappen bare
 * henvendelseslista — den ene lista som ikke endret seg av å trykke.
 *
 * Følgen var at rettelsen av P0.1 så ut til å ikke virke: valgene ble skrevet, og skjermen viste
 * det den hadde hentet før trykket. Man måtte laste siden på nytt for å se sitt eget arbeid.
 */
const ADVANCE_TOUCHES = (orgId: string) =>
  [qk.agencyEnquiries(orgId), qk.performanceAdvance(orgId), qk.attachments(orgId)] as const;

export interface AnswerInput {
  enquiryId: string;
  body: AnswerBookingEnquiryRequest;
}

/**
 * SVARET SKRIVER OGSÅ I SAMTALEN, så det er to prefikser og ikke ett.
 *
 * Atle, 9. september: «Når en artist får en forespørsel skal de svare og bekrefte i "samme" chat
 * som arrangøren sendte forespørselen i.» Serveren legger svaret i tråden i den samme forespørselen
 * — men uten `qk.engagementChat` her stod chatten i dialogen man nettopp svarte i uendret, og
 * meldingen dukket først opp om man lukket og åpnet på nytt. Målt i nettleseren: raden fikk «LEDIG»
 * med en gang, tråden fikk ingenting.
 */
export function useAnswerEnquiry(
  orgId: string,
): UseMutationResult<BookingEnquiryDto, unknown, AnswerInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ enquiryId, body }: AnswerInput) =>
      apiRequest({
        path: `${PATH}/${enquiryId}/answer`,
        method: 'PATCH',
        body,
        schema: bookingEnquiryDtoSchema,
      }),
    /*
     * SVARET SKRIVES RETT INN I LISTA (QA runde 5, punkt 9).
     *
     * ---------------------------------------------------------------------------------------------
     * Rapporten: «Stegvisningen henger etter. Etter "Send svaret" står den fortsatt på "Spurt".»
     *
     * Dataene var riktige hele tiden — målt: svaret setter `status = 'answered'` og `answered_at`
     * i samme kall. Det som hang etter var CACHEN: dialogen leser raden ut av lista, og lista ble
     * bare invalidert. Mellom svaret og at refetchen landet sto steget på det gamle.
     *
     * SVARET ER DEN NYE TILSTANDEN, så det er ingen grunn til å hente den en gang til for å få vite
     * noe vi nettopp fikk vite. Invalideringen blir stående bak for den vanlige grunnen: en annen
     * fane, og de andre listene som nevner den samme raden.
     */
    /*
     * BARE LISTENE, OG DEN ENE RADEN FOR SEG (QA runde 1 av spillesteder, P1-3).
     *
     * Her sto hele prefikset `agency-enquiries`, og under det ligger også én forespørsel, advancen
     * for forespørselen (ofte null) og advancen på sin id. `current.items.map` kastet på dem, og
     * TanStack gjorde det vellykkede svaret til en feil: «Handlingen ble ikke fullført» under
     * «Dere har godtatt budet», og plukkeren som skulle åpnes kom aldri.
     */
    onSuccess: (answered) => {
      queryClient.setQueriesData<BookingEnquiryListResponse>(
        { queryKey: [...qk.agencyEnquiries(orgId), 'list'] },
        (current) =>
          current === undefined || !Array.isArray(current.items)
            ? current
            : {
                ...current,
                items: current.items.map((row) => (row.id === answered.id ? answered : row)),
              },
      );
      if (queryClient.getQueryData(qk.agencyEnquiry(orgId, answered.id)) !== undefined) {
        queryClient.setQueryData(qk.agencyEnquiry(orgId, answered.id), answered);
      }
    },
    /*
     * QA13k F4: byråets internrom viser svarkortet fra bookingtråden, og må hentes på nytt.
     * Klikkrunde 2 (k8): godta/avslå her går gjennom avtalearket, som står i den samme dialogen.
     */
    meta: {
      invalidates: [
        qk.agencyEnquiries(orgId),
        qk.engagementChat(orgId),
        qk.internalChat(orgId),
        qk.dealSheets(orgId),
      ],
    },
  });
}

export interface AssignInput {
  enquiryId: string;
  body: AssignBookingEnquiryRequest;
}

export function useAssignEnquiry(
  orgId: string,
): UseMutationResult<BookingEnquiryDto, unknown, AssignInput> {
  return useMutation({
    mutationFn: ({ enquiryId, body }: AssignInput) =>
      apiRequest({
        path: `${PATH}/${enquiryId}/assignee`,
        method: 'PATCH',
        body,
        schema: bookingEnquiryDtoSchema,
      }),
    /*
     * Den nye ansvarlige følger trådene i saken (0950, K2 F2), så trådene og tallene i Innboks er
     * også endret — ikke bare lista.
     */
    meta: {
      invalidates: [qk.agencyEnquiries(orgId), qk.threads(orgId), qk.inboxUnreadCounts(orgId)],
    },
  });
}

/** «Del reisefølget» (sak 212): arrangøren får hente reisefølget, også uten et ja i Tvitvi. */
export function useShareParty(
  orgId: string,
): UseMutationResult<BookingEnquiryDto, unknown, string> {
  return useMutation({
    mutationFn: (enquiryId: string) =>
      apiRequest({
        path: `${PATH}/${enquiryId}/share-party`,
        method: 'POST',
        schema: bookingEnquiryDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

/* --- ⋮-menyens to skrivinger (ARTISTS.md §3.2, migrering 0115/0117) --- */

export interface SetEnquiryFormatInput {
  enquiryId: string;
  bookingArtistFormatId: string | null;
}

/** Å bytte størrelse på en forespørsel. `null` er et ekte valg: «ingen har bestemt seg». */
export function useSetEnquiryFormat(
  orgId: string,
): UseMutationResult<BookingEnquiryDto, unknown, SetEnquiryFormatInput> {
  return useMutation({
    mutationFn: ({ enquiryId, bookingArtistFormatId }: SetEnquiryFormatInput) =>
      apiRequest({
        path: `${PATH}/${enquiryId}/format`,
        method: 'PATCH',
        body: { bookingArtistFormatId },
        schema: bookingEnquiryDtoSchema,
      }),
    /*
     * BÅDE INNBOKSEN OG ROSTEREN. Formatet står under artistnavnet i lista OG i formatkolonnen i
     * bookingfanen, og den siste leser rosterens formater for å finne reisefølget. Å bare feie
     * innboksen ville latt bookingfanen vise den gamle størrelsen til noen lastet siden på nytt.
     */
    meta: { invalidates: [qk.agencyEnquiries(orgId), qk.artistRosterAll(orgId)] },
  });
}

export interface ArchiveEnquiryInput {
  enquiryId: string;
  archived: boolean;
}

/** Å legge en forespørsel bort, eller hente den fram igjen. Sier ingenting om forhandlingen. */
export function useArchiveEnquiry(
  orgId: string,
): UseMutationResult<BookingEnquiryDto, unknown, ArchiveEnquiryInput> {
  return useMutation({
    mutationFn: ({ enquiryId, archived }: ArchiveEnquiryInput) =>
      apiRequest({
        path: `${PATH}/${enquiryId}/archive`,
        method: 'PATCH',
        body: { archived },
        schema: bookingEnquiryDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

/* --- Den delte advancen (0118, ARTISTS.md §4.1 og §4.4) --- */

/**
 * ADVANCEN FOR ETT ENGASJEMENT — opprett den, eller bli med i den.
 *
 * ÉN MUTASJON OG IKKE TO. «Finnes den?» er et kappløp: to hus som trykker samtidig ville begge
 * fått nei og begge opprettet. Serveren avgjør, og svaret sier hva som skjedde.
 */
export function useOpenAdvance(
  orgId: string,
): UseMutationResult<SharedAdvanceDto, unknown, string> {
  return useMutation({
    mutationFn: (enquiryId: string) =>
      apiRequest({
        path: `${PATH}/${enquiryId}/advance`,
        method: 'POST',
        schema: sharedAdvanceDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

/**
 * ADVANCEN FOR DETTE ENGASJEMENTET, om noen har laget den. Leser, oppretter aldri.
 *
 * Skjermen trenger svaret for å vite hva knappen skal si: «Lag advancen» før den finnes, «Åpne
 * advancen» etterpå. Atle, 11. september: «peker ikke bookingen nå på advancen lengre, men på
 * samme pop up der forespørsler kommer inn etter at jeg har åpnet og startet advancen».
 */
export function advanceForEnquiryQueryOptions(orgId: string, enquiryId: string) {
  return queryOptions({
    queryKey: [...qk.agencyEnquiries(orgId), 'advance-for', enquiryId] as const,
    queryFn: ({ signal }): Promise<SharedAdvanceDto | null> =>
      apiRequest({
        path: `${PATH}/${enquiryId}/advance`,
        schema: sharedAdvanceDtoSchema.nullable(),
        signal,
      }),
    ...CACHE.detail,
  });
}

/**
 * ADVANCEN PÅ SIN EGEN ID (QA K2).
 *
 * Byråets advance-side åpnes fra en adresse som bærer ADVANCEN, ikke programposten — §4.1 lar
 * byrået lage advancen før festivalen har programmert artisten, og da finnes ingen programpost.
 * Svaret sier om det finnes en `performanceId` ennå; skjermen velger side ut fra det.
 */
export function advanceQueryOptions(orgId: string, advanceId: string) {
  return queryOptions({
    queryKey: [...qk.agencyEnquiries(orgId), 'advance', advanceId] as const,
    queryFn: ({ signal }): Promise<SharedAdvanceDto> =>
      apiRequest({
        path: `${PATH}/advances/${advanceId}`,
        schema: sharedAdvanceDtoSchema,
        signal,
      }),
    ...CACHE.detail,
  });
}

/* --- Plukkerens svar (§4.4) --- */

/**
 * HVA DETTE HUSET TAR MED INN I ADVANCEN.
 *
 * Svarer med HODET og ikke med et innhold: advancen ER festivalens programpost (0133), og den
 * leses av programpostens egne spørringer. En egen kopi her ville vært duplikatet på nytt.
 */
/**
 * PLUKKERENS EGEN KNAPP: opprett advancen OG skriv valgene (QA P0.2).
 *
 * Fram til 11. september opprettet et godtatt bud advancen FØR plukkeren ble tegnet, og «Ikke
 * nå» lot raden bli stående. Kommentaren i `EngagementDialog` påsto det motsatte. Nå finnes
 * raden først når noen trykker «Opprett advancen», som er hva den knappen alltid har sagt.
 */
export function useEnterAdvanceByEnquiry(
  orgId: string,
  enquiryId: string,
): UseMutationResult<SharedAdvanceDto, unknown, EnterAdvanceRequest> {
  return useMutation({
    mutationFn: (body: EnterAdvanceRequest): Promise<SharedAdvanceDto> =>
      apiRequest({
        path: `${PATH}/${enquiryId}/advance/entry`,
        method: 'POST',
        body,
        schema: sharedAdvanceDtoSchema,
      }),
    meta: { invalidates: [...ADVANCE_TOUCHES(orgId)] },
  });
}
export function useEnterAdvance(
  orgId: string,
  advanceId: string,
): UseMutationResult<SharedAdvanceDto, unknown, EnterAdvanceRequest> {
  return useMutation({
    mutationFn: (body: EnterAdvanceRequest) =>
      apiRequest({
        path: `${PATH}/advances/${advanceId}/entry`,
        method: 'POST',
        body,
        schema: sharedAdvanceDtoSchema,
      }),
    meta: { invalidates: [...ADVANCE_TOUCHES(orgId)] },
  });
}
